/**
 * 租户上下文
 * 管理租户配置、主题、功能开关等
 * 【安全修复】统一两个项目的 TenantContext 实现，增强安全性
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import * as tenantApi from '../api/tenant';

// 默认主题配置
const DEFAULT_THEME = {
    primaryColor: '#2563eb',      // 主色
    secondaryColor: '#64748b',    // 辅色
    successColor: '#10b981',      // 成功色
    warningColor: '#f59e0b',      // 警告色
    errorColor: '#ef4444',        // 错误色
    backgroundColor: '#f8fafc',   // 背景色
    textColor: '#1e293b',         // 文字色
    borderRadius: '8px',          // 圆角
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
};

// 默认功能开关
const DEFAULT_FEATURES = {
    aiReview: true,           // AI审核
    manualAnnotation: true,   // 人工标注
    manualReview: true,       // 人工复核
    reportGenerate: true,     // 报告生成
    batchExport: true,        // 批量导出
    customTheme: false,       // 自定义主题（高级版）
    apiAccess: false,         // API访问（企业版）
};

// 默认页面配置
const DEFAULT_PAGE_CONFIG = {
    logo: null,
    faviconUrl: null,
    tenantName: '食品数字标签系统',
    showBreadcrumb: true,
    showFooter: true,
    sidebarWidth: 240,
    headerHeight: 64,
};

// 初始状态
const initialState = {
    isLoading: true,
    tenantId: null,
    enterpriseId: null,
    tenant: null,
    theme: DEFAULT_THEME,
    features: DEFAULT_FEATURES,
    pageConfig: DEFAULT_PAGE_CONFIG,
    quota: null,
    enterprises: [],
    error: null,
};

// Action 类型
const TENANT_ACTIONS = {
    SET_LOADING: 'SET_LOADING',
    SET_TENANT_ID: 'SET_TENANT_ID',
    SET_ENTERPRISE_ID: 'SET_ENTERPRISE_ID',
    SET_TENANT: 'SET_TENANT',
    SET_THEME: 'SET_THEME',
    SET_FEATURES: 'SET_FEATURES',
    SET_PAGE_CONFIG: 'SET_PAGE_CONFIG',
    SET_QUOTA: 'SET_QUOTA',
    SET_ENTERPRISES: 'SET_ENTERPRISES',
    SET_ERROR: 'SET_ERROR',
    RESET: 'RESET',
};

// Reducer
function tenantReducer(state, action) {
    switch (action.type) {
        case TENANT_ACTIONS.SET_LOADING:
            return { ...state, isLoading: action.payload };

        case TENANT_ACTIONS.SET_TENANT_ID:
            return { ...state, tenantId: action.payload };

        case TENANT_ACTIONS.SET_ENTERPRISE_ID:
            return { ...state, enterpriseId: action.payload };

        case TENANT_ACTIONS.SET_TENANT:
            return {
                ...state,
                isLoading: false,
                tenant: action.payload,
                error: null,
            };

        case TENANT_ACTIONS.SET_THEME:
            return {
                ...state,
                theme: { ...DEFAULT_THEME, ...action.payload },
            };

        case TENANT_ACTIONS.SET_FEATURES:
            return {
                ...state,
                features: { ...DEFAULT_FEATURES, ...action.payload },
            };

        case TENANT_ACTIONS.SET_PAGE_CONFIG:
            return {
                ...state,
                pageConfig: { ...DEFAULT_PAGE_CONFIG, ...action.payload },
            };

        case TENANT_ACTIONS.SET_QUOTA:
            return { ...state, quota: action.payload };

        case TENANT_ACTIONS.SET_ENTERPRISES:
            return { ...state, enterprises: action.payload };

        case TENANT_ACTIONS.SET_ERROR:
            return { ...state, error: action.payload, isLoading: false };

        case TENANT_ACTIONS.RESET:
            return initialState;

        default:
            return state;
    }
}

// 创建 Context
const TenantContext = createContext(null);

/**
 * TenantProvider 组件
 */
export function TenantProvider({ children }) {
    const [state, dispatch] = useReducer(tenantReducer, initialState);

    // 加载租户信息
    const loadTenantInfo = useCallback(async (tenantId) => {
        if (!tenantId) {
            dispatch({ type: TENANT_ACTIONS.SET_LOADING, payload: false });
            return;
        }

        dispatch({ type: TENANT_ACTIONS.SET_LOADING, payload: true });

        try {
            // 获取租户信息
            const tenant = await tenantApi.getTenantInfo(tenantId).catch(() => null);

            // 获取租户配置（配额等）
            const config = await tenantApi.getTenantConfig(tenantId).catch(() => null);

            if (tenant) {
                dispatch({ type: TENANT_ACTIONS.SET_TENANT, payload: tenant });
                dispatch({
                    type: TENANT_ACTIONS.SET_PAGE_CONFIG,
                    payload: { tenantName: tenant.tenantName },
                });
            }

            if (config) {
                dispatch({
                    type: TENANT_ACTIONS.SET_QUOTA,
                    payload: {
                        aiUsed: config.aiUsedCount || 0,
                        aiTotal: config.aiQuota || 0,
                        storageUsed: config.storageUsed || 0,
                        storageTotal: config.storageQuota || 0,
                    },
                });

                // 设置主题颜色
                if (config.themeColor) {
                    dispatch({
                        type: TENANT_ACTIONS.SET_THEME,
                        payload: { primaryColor: config.themeColor },
                    });
                }
            }
        } catch (error) {
            console.error('Failed to load tenant info:', error);
            dispatch({ type: TENANT_ACTIONS.SET_ERROR, payload: error.message });
        }
    }, []);

    // 初始化租户ID和企业ID
    useEffect(() => {
        const tenantId = tenantApi.getCurrentTenantId();
        const enterpriseId = tenantApi.getCurrentEnterpriseId();

        if (tenantId) {
            dispatch({ type: TENANT_ACTIONS.SET_TENANT_ID, payload: tenantId });
        }
        if (enterpriseId) {
            dispatch({ type: TENANT_ACTIONS.SET_ENTERPRISE_ID, payload: enterpriseId });
        }

        // 如果有tenantId，加载租户信息
        if (tenantId) {
            loadTenantInfo(tenantId);
        } else {
            dispatch({ type: TENANT_ACTIONS.SET_LOADING, payload: false });
        }
    }, [loadTenantInfo]);

    // 监听登录成功事件，刷新租户信息
    useEffect(() => {
        const handleLoginSuccess = (event) => {
            const user = event.detail;
            if (user?.tenantId) {
                dispatch({ type: TENANT_ACTIONS.SET_TENANT_ID, payload: user.tenantId });
                if (user.enterpriseId) {
                    dispatch({ type: TENANT_ACTIONS.SET_ENTERPRISE_ID, payload: user.enterpriseId });
                }
                loadTenantInfo(user.tenantId);
            }
        };

        window.addEventListener('auth:login-success', handleLoginSuccess);
        return () => {
            window.removeEventListener('auth:login-success', handleLoginSuccess);
        };
    }, [loadTenantInfo]);

    // 应用主题到 CSS 变量
    useEffect(() => {
        const root = document.documentElement;
        Object.entries(state.theme).forEach(([key, value]) => {
            const cssVar = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
            root.style.setProperty(cssVar, value);
        });
    }, [state.theme]);

    // 切换租户
    // 【安全修复】使用后端验证的租户切换方法
    const switchTenant = useCallback(async (tenantId, enterpriseId) => {
        if (!tenantId) return;

        try {
            // 调用带验证的切换方法
            await tenantApi.switchTenantWithValidation(tenantId, enterpriseId);
            dispatch({ type: TENANT_ACTIONS.SET_TENANT_ID, payload: tenantId });
            if (enterpriseId) {
                dispatch({ type: TENANT_ACTIONS.SET_ENTERPRISE_ID, payload: enterpriseId });
            }
            loadTenantInfo(tenantId);
        } catch (error) {
            console.error('租户切换失败:', error);
            dispatch({ type: TENANT_ACTIONS.SET_ERROR, payload: error.message });
            throw error; // 向上抛出异常让调用者处理
        }
    }, [loadTenantInfo]);

    // 刷新租户信息
    const refreshTenant = useCallback(() => {
        const tenantId = state.tenantId || tenantApi.getCurrentTenantId();
        if (tenantId) {
            loadTenantInfo(tenantId);
        }
    }, [state.tenantId, loadTenantInfo]);

    // 检查功能是否启用
    const isFeatureEnabled = useCallback((featureName) => {
        return state.features[featureName] === true;
    }, [state.features]);

    // 检查配额是否充足
    const checkQuota = useCallback((quotaType) => {
        if (!state.quota) return true;

        switch (quotaType) {
            case 'aiUsage':
                return state.quota.aiTotal === 0 || state.quota.aiUsed < state.quota.aiTotal;
            case 'storage':
                return state.quota.storageTotal === 0 || state.quota.storageUsed < state.quota.storageTotal;
            default:
                return true;
        }
    }, [state.quota]);

    // Context 值 - 使用 useMemo 优化
    const value = useMemo(() => ({
        // 状态
        ...state,

        // 方法
        switchTenant,
        refreshTenant,
        loadTenantInfo,
        isFeatureEnabled,
        checkQuota,

        // 常量
        DEFAULT_THEME,
        DEFAULT_FEATURES,
    }), [
        state,
        switchTenant,
        refreshTenant,
        loadTenantInfo,
        isFeatureEnabled,
        checkQuota,
    ]);

    return (
        <TenantContext.Provider value={value}>
            {children}
        </TenantContext.Provider>
    );
}

/**
 * 使用租户上下文的 Hook
 */
export function useTenant() {
    const context = useContext(TenantContext);
    if (!context) {
        throw new Error('useTenant must be used within TenantProvider');
    }
    return context;
}

/**
 * useTheme Hook - 只获取主题相关
 */
export function useTheme() {
    const { theme } = useTenant();
    return { theme };
}

/**
 * useFeatures Hook - 只获取功能开关相关
 */
export function useFeatures() {
    const { features, isFeatureEnabled } = useTenant();
    return { features, isFeatureEnabled };
}

/**
 * useQuota Hook - 只获取配额相关
 */
export function useQuota() {
    const { quota, checkQuota } = useTenant();
    return { quota, checkQuota };
}

export default TenantContext;

